import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, ScanLine, CheckCircle, MapPin, AlertCircle, RefreshCw, QrCode, List, StopCircle, Info, CheckCircle2, Circle, Clock, SkipForward, AlertTriangle, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const FGPutAwayMobile = () => {
  const navigate = useNavigate();
  
  // Active tab: 'my-slips' or 'gt-scan'
  const [activeTab, setActiveTab] = useState('my-slips');
  
  // Bundle scan state (for GT Scan Bundle tab)
  const [bundleCode, setBundleCode] = useState('');
  const [isScanningBundle, setIsScanningBundle] = useState(false);
  
  // Put Away state
  const [mySlips, setMySlips] = useState([]);
  const [activeSlip, setActiveSlip] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [scanMode, setScanMode] = useState('bin');
  const [scanInput, setScanInput] = useState('');
  const [binInfo, setBinInfo] = useState(null);
  const [markBinFullDialog, setMarkBinFullDialog] = useState(false);
  const [isMarkingBinFull, setIsMarkingBinFull] = useState(false);
  
  // TROLLEY MODE - Shows all items at once
  const [trolleyMode, setTrolleyMode] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [overrideItem, setOverrideItem] = useState(null);
  const [overrideBinCode, setOverrideBinCode] = useState('');
  
  // Skip Item Dialog
  const [showSkipDialog, setShowSkipDialog] = useState(false);
  const [skipItem, setSkipItem] = useState(null);
  const [skipReason, setSkipReason] = useState('');
  const [skipReasonDetails, setSkipReasonDetails] = useState('');
  const [isSkipping, setIsSkipping] = useState(false);
  const [skipReasons, setSkipReasons] = useState([]);
  
  // Progress view state (for session recovery)
  const [showProgressView, setShowProgressView] = useState(false);
  const [progressData, setProgressData] = useState(null);
  
  // Bin conflict handling state
  const [binConflictError, setBinConflictError] = useState(null);
  const [isRequestingAlternative, setIsRequestingAlternative] = useState(false);

  // Multi-staff team (2026-07): optional. When 1+ staff are scanned here, the
  // put-away pieces are credited to them (round-robin) instead of the slip's
  // assigned picker. Empty → slip owner credited (unchanged).
  const [staffTeam, setStaffTeam] = useState([]); // [{staff_id, staff_name}]
  const [staffTeamInput, setStaffTeamInput] = useState('');
  const [isAddingStaff, setIsAddingStaff] = useState(false);
  const [placedCount, setPlacedCount] = useState(0); // session-wide rotation index

  const teamStaffParams = () => (
    staffTeam.length >= 1
      ? { staff_ids: staffTeam.map((s) => s.staff_id).join(','), staff_index: placedCount }
      : {}
  );

  const handleAddTeamStaff = async () => {
    const id = staffTeamInput.trim();
    if (!id || isAddingStaff) return;
    if (staffTeam.some((s) => s.staff_id === id)) {
      toast.info('Staff already added');
      setStaffTeamInput('');
      return;
    }
    setIsAddingStaff(true);
    try {
      const resp = await apiClient.get(`/validate/staff/${id}`);
      if (resp.data.valid) {
        setStaffTeam((prev) => [...prev, { staff_id: id, staff_name: resp.data.staff_name }]);
        setStaffTeamInput('');
        toast.success(`Added ${resp.data.staff_name}`);
      } else {
        toast.error('Invalid Staff ID');
      }
    } catch (error) {
      toast.error('Staff validation failed');
    } finally {
      setIsAddingStaff(false);
    }
  };

  const handleRemoveTeamStaff = (id) => setStaffTeam((prev) => prev.filter((s) => s.staff_id !== id));

  const bundleInputRef = useRef(null);
  const scanInputRef = useRef(null);
  
  useEffect(() => {
    loadMySlips();
    fetchSkipReasons();
  }, []);

  const fetchSkipReasons = async () => {
    try {
      const response = await apiClient.get('/wms/fg/put-away/skip-reasons');
      setSkipReasons(response.data.reasons || []);
    } catch (error) {
      console.error('Error fetching skip reasons:', error);
      // Default reasons as fallback
      setSkipReasons([
        "Damaged pieces",
        "Wrong barcode / mismatch", 
        "Missing pieces",
        "Quality issue",
        "Item not found",
        "Bin not accessible",
        "Other"
      ]);
    }
  };

  const handleSkipItem = async () => {
    if (!skipItem || !skipReason) {
      toast.error('Please select a reason');
      return;
    }
    
    setIsSkipping(true);
    try {
      const response = await apiClient.post('/wms/fg/put-away/skip-item', null, {
        params: {
          slip_id: activeSlip.slip_id,
          item_id: skipItem.item_id,
          reason: skipReason,
          reason_details: skipReasonDetails || null
        }
      });
      
      toast.success(response.data.message);
      
      // Update the item locally
      setActiveSlip(prev => ({
        ...prev,
        items: prev.items.map(itm => 
          itm.item_id === skipItem.item_id 
            ? { ...itm, status: response.data.item_status, skipped_qty: response.data.skipped_qty }
            : itm
        )
      }));
      
      // Reset skip dialog
      setShowSkipDialog(false);
      setSkipItem(null);
      setSkipReason('');
      setSkipReasonDetails('');
      
      // In trolley mode, reset selection
      if (trolleyMode) {
        setTrolleyActiveItem(null);
        setTrolleyBinLocked(false);
        setTrolleyScanMode('select_item');
      }
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to skip item');
    } finally {
      setIsSkipping(false);
    }
  };

  const openSkipDialog = (item) => {
    setSkipItem(item);
    setSkipReason('');
    setSkipReasonDetails('');
    setShowSkipDialog(true);
  };
  
  useEffect(() => {
    if (activeTab === 'gt-scan' && !activeSlip) {
      setTimeout(() => bundleInputRef.current?.focus(), 100);
    }
  }, [activeTab, activeSlip]);
  
  useEffect(() => {
    if (scanInputRef.current && activeSlip) {
      scanInputRef.current.focus();
    }
  }, [activeSlip, scanMode]);
  
  const loadMySlips = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/wms/fg/put-away/my-slips');
      setMySlips(response.data.slips || []);
    } catch (error) {
      console.error('Error loading slips:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleScanBundle = async () => {
    if (!bundleCode.trim()) {
      toast.error('Please scan a bundle barcode');
      return;
    }
    
    setIsScanningBundle(true);
    try {
      const response = await apiClient.post('/wms/fg/put-away/scan-bundle-to-start', null, {
        params: { bundle_code: bundleCode.trim() }
      });
      
      if (response.data.success) {
        const slip = response.data.slip;
        setActiveSlip(slip);
        const pendingItem = slip.items?.find(i => i.status === 'pending' || i.status === 'in_progress');
        // Fallback to first item if no pending/in_progress item found
        const itemToUse = pendingItem || slip.items?.[0];
        if (itemToUse) {
          setActiveItem(itemToUse);
          
          // Determine scan mode based on state:
          // 1. If already has actual_bin → scan USN to place items
          // 2. If has suggested_bin but no actual_bin → scan bin first
          // 3. If no suggested_bin → scan USN first to determine bin
          if (itemToUse.actual_bin) {
            setScanMode('usn');
          } else if (itemToUse.suggested_bin) {
            setScanMode('bin');
          } else {
            // No suggested bin - scan USN first to get bin suggestion
            setScanMode('usn_first');
            toast.info('Scan first USN to get bin suggestion', {
              description: 'Bin will be suggested based on the USN you scan'
            });
          }
          
          // Notify user if bin was changed due to revalidation
          if (itemToUse.previous_suggested_bin && itemToUse.previous_suggested_bin !== itemToUse.suggested_bin) {
            toast.warning(`Bin changed: ${itemToUse.previous_suggested_bin} → ${itemToUse.suggested_bin || 'No bin available'}`, {
              description: itemToUse.revalidation_reason || 'Original bin is now occupied by a different item'
            });
          }
        } else {
          // No items in slip - this shouldn't happen but handle gracefully
          toast.warning('Slip has no items to put away');
        }
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process bundle');
      setBundleCode('');
      bundleInputRef.current?.focus();
    } finally {
      setIsScanningBundle(false);
    }
  };
  
  // Fetch progress data for a slip
  const fetchSlipProgress = async (slipId) => {
    try {
      const response = await apiClient.get(`/wms/fg/put-away/slip-progress/${slipId}`);
      setProgressData(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching slip progress:', error);
      return null;
    }
  };
  
  // Continue from progress view
  const continueFromProgress = async () => {
    setShowProgressView(false);
    if (activeSlip) {
      // Start the slip normally
      await handleStartSlipDirect(activeSlip);
    }
  };
  
  // Refresh progress data
  const refreshProgress = async () => {
    if (activeSlip) {
      setIsLoading(true);
      await fetchSlipProgress(activeSlip.slip_id);
      setIsLoading(false);
    }
  };
  
  const handleStartSlip = async (slip) => {
    // Check if slip is in_progress - show progress view first
    if (slip.status === 'in_progress') {
      setActiveSlip(slip);
      setIsLoading(true);
      const progress = await fetchSlipProgress(slip.slip_id);
      setIsLoading(false);
      
      if (progress && (progress.completed_usns.length > 0 || progress.pending_usns.length > 0)) {
        setShowProgressView(true);
        return;
      }
    }
    
    // Normal start flow
    await handleStartSlipDirect(slip);
  };
  
  // TROLLEY MODE: Start slip and pre-calculate all bins
  const handleStartTrolleyMode = async (slip) => {
    setIsLoading(true);
    try {
      const response = await apiClient.post(`/wms/fg/put-away/start-slip-trolley/${slip.slip_id}`);
      
      const updatedSlip = response.data.slip || slip;
      setActiveSlip(updatedSlip);
      setTrolleyMode(true);
      setTrolleyScanMode('select_item'); // Start by selecting an item
      setTrolleyActiveItem(null);
      setTrolleyBinLocked(false);
      
      // Count items with bin suggestions
      const itemsWithBins = updatedSlip.items?.filter(i => i.suggested_bin).length || 0;
      const totalItems = updatedSlip.items?.length || 0;
      
      toast.success(`Trolley mode ready! ${itemsWithBins}/${totalItems} items have bins pre-assigned.`, {
        description: 'Tap an item → Scan bin → Scan USNs'
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trolley mode');
    } finally {
      setIsLoading(false);
    }
  };

  // Trolley mode states
  const [trolleyScanMode, setTrolleyScanMode] = useState('select_item'); // select_item, scan_bin, scan_usn
  const [trolleyActiveItem, setTrolleyActiveItem] = useState(null);
  const [trolleyBinLocked, setTrolleyBinLocked] = useState(false);

  // Handle selecting an item in trolley mode
  const handleTrolleySelectItem = (item) => {
    if (item.status === 'completed') {
      toast.info('This item is already completed');
      return;
    }
    
    setTrolleyActiveItem(item);
    
    // If item already has actual_bin locked, go to USN scan mode
    if (item.actual_bin) {
      setTrolleyBinLocked(true);
      setTrolleyScanMode('scan_usn');
      toast.info(`Bin ${item.actual_bin} already locked. Scan USNs to place.`);
    } else {
      // Need to scan bin first
      setTrolleyBinLocked(false);
      setTrolleyScanMode('scan_bin');
      toast.info(`Scan bin ${item.suggested_bin || 'location'} to confirm`);
    }
    
    setScanInput('');
    setTimeout(() => scanInputRef.current?.focus(), 100);
  };

  // Handle bin scan in trolley mode
  const handleTrolleyBinScan = async (binCode) => {
    if (!trolleyActiveItem) {
      toast.error('Please select an item first');
      return;
    }
    
    try {
      const response = await apiClient.post('/wms/fg/put-away/scan-bin', null, {
        params: { slip_id: activeSlip.slip_id, item_id: trolleyActiveItem.item_id, bin_code: binCode }
      });
      
      // Update the item with actual bin
      setTrolleyActiveItem(prev => ({ ...prev, actual_bin: binCode }));
      setTrolleyBinLocked(true);
      setTrolleyScanMode('scan_usn');
      
      // Update in activeSlip items too
      setActiveSlip(prev => ({
        ...prev,
        items: prev.items.map(itm => 
          itm.item_id === trolleyActiveItem.item_id 
            ? { ...itm, actual_bin: binCode }
            : itm
        )
      }));
      
      const placedCount = trolleyActiveItem.placed_usns?.length || 0;
      toast.success(`Bin ${binCode} locked! Now scan USNs (${placedCount}/${trolleyActiveItem.qty})`);
      setScanInput('');
      setTimeout(() => scanInputRef.current?.focus(), 100);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid bin');
      setScanInput('');
      scanInputRef.current?.focus();
    }
  };

  // Handle USN scan in trolley mode (after bin is locked)
  const handleTrolleyUsnScanNew = async (usnCode) => {
    if (!trolleyActiveItem) {
      toast.error('Please select an item first');
      return;
    }
    
    if (!trolleyBinLocked) {
      toast.error('Please scan the bin first to confirm location');
      setTrolleyScanMode('scan_bin');
      return;
    }
    
    try {
      const response = await apiClient.post('/wms/fg/put-away/scan-usn', null, {
        params: { slip_id: activeSlip.slip_id, item_id: trolleyActiveItem.item_id, usn_code: usnCode, ...teamStaffParams() }
      });
      
      if (response.data.success === false) {
        toast.error(response.data.message || 'Failed to place USN');
        setScanInput('');
        scanInputRef.current?.focus();
        return;
      }
      setPlacedCount((c) => c + 1);
      
      // Update placed count
      const newPlacedUsns = [...(trolleyActiveItem.placed_usns || []), usnCode];
      setTrolleyActiveItem(prev => ({
        ...prev,
        placed_usns: newPlacedUsns,
        status: response.data.item_complete ? 'completed' : 'in_progress'
      }));
      
      // Update in activeSlip items too
      setActiveSlip(prev => ({
        ...prev,
        items: prev.items.map(itm => 
          itm.item_id === trolleyActiveItem.item_id 
            ? { ...itm, placed_usns: newPlacedUsns, status: response.data.item_complete ? 'completed' : 'in_progress' }
            : itm
        )
      }));
      
      toast.success(`+1 placed (${response.data.placed_count}/${trolleyActiveItem.qty})`);
      
      if (response.data.item_complete) {
        toast.success(`Item complete! Select next item.`);
        setTrolleyActiveItem(null);
        setTrolleyBinLocked(false);
        setTrolleyScanMode('select_item');
      }
      
      setScanInput('');
      setTimeout(() => scanInputRef.current?.focus(), 100);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid USN');
      setScanInput('');
      scanInputRef.current?.focus();
    }
  };

  // Handle trolley mode scan input submission
  const handleTrolleyScanSubmit = () => {
    if (!scanInput.trim()) return;
    
    if (trolleyScanMode === 'scan_bin') {
      handleTrolleyBinScan(scanInput.trim());
    } else if (trolleyScanMode === 'scan_usn') {
      handleTrolleyUsnScanNew(scanInput.trim());
    } else {
      toast.info('Please select an item from the list first');
    }
    setScanInput('');
  };
  
  // Refresh active slip data
  const refreshActiveSlip = async () => {
    if (!activeSlip) return;
    try {
      const response = await apiClient.get('/wms/fg/put-away/my-slips');
      const updatedSlip = response.data.slips?.find(s => s.slip_id === activeSlip.slip_id);
      if (updatedSlip) {
        setActiveSlip(updatedSlip);
      }
    } catch (error) {
      console.error('Failed to refresh slip:', error);
    }
  };
  
  // Handle bin override for an item
  const handleBinOverride = async () => {
    if (!overrideItem || !overrideBinCode.trim()) {
      toast.error('Please enter a bin code');
      return;
    }
    
    try {
      const response = await apiClient.post(
        `/wms/fg/put-away/override-bin/${activeSlip.slip_id}/${overrideItem.item_id}`,
        null,
        { params: { new_bin_code: overrideBinCode.trim() } }
      );
      
      if (response.data.success) {
        toast.success(`Bin updated to ${overrideBinCode}`);
        setShowOverrideDialog(false);
        setOverrideItem(null);
        setOverrideBinCode('');
        await refreshActiveSlip();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update bin');
    }
  };
  
  const handleStartSlipDirect = async (slip) => {
    try {
      // Call start-slip endpoint to revalidate bin suggestions
      const response = await apiClient.post(`/wms/fg/put-away/start-slip/${slip.slip_id}`);
      
      // Use the updated slip with revalidated bin suggestions
      const updatedSlip = response.data.slip || slip;
      setActiveSlip(updatedSlip);
      
      const pendingItem = updatedSlip.items?.find(i => i.status === 'pending' || i.status === 'in_progress');
      // Fallback to first item if no pending/in_progress item found
      const itemToUse = pendingItem || updatedSlip.items?.[0];
      if (itemToUse) {
        setActiveItem(itemToUse);
        
        // Determine scan mode based on state:
        // 1. If already has actual_bin → scan USN to place items
        // 2. If has suggested_bin but no actual_bin → scan bin first
        // 3. If no suggested_bin (multi-SKU bundle) → scan USN first to determine bin
        if (itemToUse.actual_bin) {
          setScanMode('usn');
        } else if (itemToUse.suggested_bin) {
          setScanMode('bin');
        } else {
          // No suggested bin - scan USN first to get bin suggestion
          setScanMode('usn_first');
          toast.info('Scan first USN to get bin suggestion', {
            description: 'This bundle may contain multiple SKUs'
          });
        }
        
        // Notify user if bin was changed
        if (itemToUse.previous_suggested_bin && itemToUse.previous_suggested_bin !== itemToUse.suggested_bin) {
          toast.warning(`Bin changed: ${itemToUse.previous_suggested_bin} → ${itemToUse.suggested_bin || 'No bin available'}`, {
            description: itemToUse.revalidation_reason || 'Original bin is now occupied by a different item'
          });
        }
      } else {
        toast.warning('Slip has no items to put away');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start slip');
    }
  };
  
  const handleBinScan = async (binCode) => {
    try {
      const response = await apiClient.post('/wms/fg/put-away/scan-bin', null, {
        params: { slip_id: activeSlip.slip_id, item_id: activeItem.item_id, bin_code: binCode }
      });
      
      // Set bin info with learned capacity data
      setBinInfo({
        ...response.data,
        current_qty: response.data.current_qty || 0,
        learned_capacity: response.data.learned_capacity || null,
        has_learned_capacity: !!response.data.learned_capacity
      });
      setScanMode('usn');
      setBinConflictError(null); // Clear any conflict error
      
      // Preserve placed_usns when switching bins (overflow scenario)
      setActiveItem(prev => ({ 
        ...prev, 
        actual_bin: binCode,
        // Keep existing placed_usns - they're already in the database
      }));
      
      const placedCount = response.data.placed_count || activeItem.placed_usns?.length || 0;
      const totalQty = response.data.item_qty || activeItem.qty || 0;
      
      // Show learned capacity hint if available
      if (response.data.learned_capacity) {
        toast.success(`Bin ${binCode} locked. Expected capacity: ~${response.data.learned_capacity}. Progress: ${placedCount}/${totalQty}`);
      } else {
        toast.success(`Bin ${binCode} locked. Progress: ${placedCount}/${totalQty}. Mark full when done.`);
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail || 'Invalid bin';
      // Check if this is a "different item in bin" conflict
      if (errorDetail.includes('different item') || errorDetail.includes('One item per footprint')) {
        setBinConflictError({
          message: errorDetail,
          attemptedBin: binCode
        });
        toast.error('Bin conflict! Choose an option below or scan a different bin.');
      } else {
        toast.error(errorDetail);
      }
    }
  };
  
  // Request alternative bin from server
  const handleRequestAlternativeBin = async () => {
    setIsRequestingAlternative(true);
    try {
      const params = new URLSearchParams();
      params.append('design_name', activeItem.design_name || '');
      params.append('shade', activeItem.shade || '');
      params.append('size', activeItem.size || '');
      params.append('barcode', activeItem.barcode || '');
      params.append('qty_needed', activeItem.qty - (activeItem.placed_usns?.length || 0));
      params.append('velocity_class', activeItem.velocity_class || 'C');
      
      // CRITICAL: Pass category4 for hall filtering
      if (activeItem.category4) {
        params.append('category4', activeItem.category4);
      }
      
      // Exclude the bin that caused the conflict
      if (binConflictError?.attemptedBin) {
        params.append('exclude_bins', binConflictError.attemptedBin);
      }
      
      const response = await apiClient.post(`/wms/fg/put-away/suggest-alternative-bin?${params.toString()}`);
      
      if (response.data.success && response.data.bin_code) {
        // Update the suggested bin
        setActiveItem(prev => ({
          ...prev,
          suggested_bin: response.data.bin_code
        }));
        setBinConflictError(null);
        toast.success(`New bin suggested: ${response.data.bin_code}`);
      } else {
        toast.error(response.data.suggestion_reason || 'No alternative bins available');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to find alternative bin');
    } finally {
      setIsRequestingAlternative(false);
    }
  };
  
  const handleUsnScan = async (usnCode) => {
    try {
      const response = await apiClient.post('/wms/fg/put-away/scan-usn', null, {
        params: { slip_id: activeSlip.slip_id, item_id: activeItem.item_id, usn_code: usnCode, ...teamStaffParams() }
      });
      
      // Check for SKU mismatch - different barcode in the same bin
      if (response.data.sku_mismatch) {
        const newBin = response.data.suggested_new_bin;
        const newBinInfo = response.data.suggested_bin_info;
        const existingBinFound = response.data.existing_bin_found;
        
        // Show different messages based on whether we found an existing bin
        if (existingBinFound) {
          // Guide user to existing bin that already has this SKU
          toast.warning(`Different SKU! Go to ${newBin}`, {
            description: `This item (${response.data.usn_sku}) already exists in bin ${newBin}`,
            duration: 6000
          });
        } else {
          // New SKU needs a new empty bin
          toast.warning(`Different SKU detected!`, {
            description: `${response.data.usn_sku} needs new bin: ${newBin || 'Not available'}`,
            duration: 6000
          });
        }
        
        if (newBin) {
          // Update the item with the new suggested bin and clear current bin
          setActiveItem(prev => ({
            ...prev,
            actual_bin: null,
            suggested_bin: newBin,
            suggested_bin_info: newBinInfo
          }));
          
          // Clear bin info and switch to bin scan mode
          setBinInfo(null);
          setScanMode('bin');
          
          // Show bin hierarchy if available
          if (newBinInfo?.bin_hierarchy) {
            toast.info(newBinInfo.bin_hierarchy, { duration: 5000 });
          }
        } else {
          toast.error('No alternative bin available. Please contact supervisor.');
        }
        return;
      }
      
      // Check if response was not successful for other reasons
      if (response.data.success === false) {
        toast.error(response.data.message || 'Failed to place USN');
        return;
      }
      setPlacedCount((c) => c + 1);
      
      // Update bin info with new count and learned capacity
      if (response.data.bin_qty_after_placement) {
        setBinInfo(prev => ({
          ...prev,
          current_qty: response.data.bin_qty_after_placement,
          learned_capacity: response.data.expected_capacity,
          has_learned_capacity: response.data.has_learned_capacity,
          approaching_capacity: response.data.approaching_capacity,
          capacity_hint: response.data.capacity_hint
        }));
      }
      
      // Show hint when approaching learned capacity (soft warning, not blocking)
      if (response.data.approaching_capacity) {
        toast.info(response.data.capacity_hint, { duration: 4000 });
      }
      
      setActiveItem(prev => ({
        ...prev,
        placed_usns: [...(prev.placed_usns || []), usnCode],
        status: response.data.item_complete ? 'completed' : 'in_progress'
      }));
      
      // Also update the item status in activeSlip.items to ensure handleNextItem works correctly
      if (response.data.item_complete) {
        setActiveSlip(prev => ({
          ...prev,
          items: prev.items.map(itm => 
            itm.item_id === activeItem.item_id 
              ? { ...itm, status: 'completed', placed_usns: [...(itm.placed_usns || []), usnCode] }
              : itm
          )
        }));
      }
      
      toast.success(`+1 placed (${response.data.placed_count}/${activeItem.qty})`);
      
      if (response.data.item_complete) {
        handleNextItem();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid USN');
    }
  };
  
  // Release bin lock when going back without placing any USN
  const releaseBinLockIfNeeded = async () => {
    if (activeSlip && activeItem && activeItem.actual_bin) {
      // Check if any USN was placed
      const placedCount = activeItem.placed_usns?.length || 0;
      if (placedCount === 0) {
        try {
          await apiClient.post('/wms/fg/put-away/release-bin-lock', null, {
            params: { slip_id: activeSlip.slip_id, item_id: activeItem.item_id }
          });
          console.log('Bin lock released');
        } catch (error) {
          console.warn('Could not release bin lock:', error);
        }
      }
    }
  };
  
  // Handle going back from put-away mode
  const handleGoBack = async () => {
    await releaseBinLockIfNeeded();
    setActiveSlip(null);
    setActiveItem(null);
    setBinInfo(null);
    setBundleCode('');
    setBinConflictError(null);
  };
  
  // Handle marking bin as full - records learned capacity and switches to new bin
  const handleMarkBinFull = async () => {
    if (!activeSlip || !activeItem || !activeItem.actual_bin) {
      toast.error('No bin is currently active');
      return;
    }
    
    setIsMarkingBinFull(true);
    try {
      const response = await apiClient.post('/wms/fg/put-away/mark-bin-full', null, {
        params: { slip_id: activeSlip.slip_id, item_id: activeItem.item_id }
      });
      
      toast.success(`Bin full! Learned: ${response.data.learned_capacity} pieces for ${response.data.design_name} in ${response.data.bin_type} bins`);
      
      // Update active item to clear the bin and show new suggestion
      setActiveItem(prev => ({
        ...prev,
        actual_bin: null,
        suggested_bin: response.data.suggested_new_bin
      }));
      
      // Clear bin info and switch to bin scan mode
      setBinInfo(null);
      setScanMode('bin');
      setMarkBinFullDialog(false);
      
      // If item is complete, move to next item
      if (response.data.item_complete) {
        handleNextItem();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark bin as full');
    } finally {
      setIsMarkingBinFull(false);
    }
  };
  
  const handleNextItem = () => {
    // Re-fetch items from activeSlip to get the latest status
    const items = activeSlip.items || [];
    const currentItemId = activeItem?.item_id;
    
    // Find next item that is NOT completed and NOT the current item
    const nextItem = items.find(i => 
      i.item_id !== currentItemId && 
      i.status !== 'completed'
    );
    
    if (nextItem) {
      setActiveItem(nextItem);
      setScanMode(nextItem.actual_bin ? 'usn' : 'bin');
      setBinInfo(null);
      toast.info(`Moving to next item: ${nextItem.design_name}`);
    } else {
      // All items completed - complete the slip
      handleCompleteSlip();
    }
  };
  
  const handleCompleteSlip = async () => {
    try {
      const response = await apiClient.post(`/wms/fg/put-away/complete-slip/${activeSlip.slip_id}`);
      toast.success('Put away completed!');
      
      // Check if next slip was auto-assigned
      if (response.data.next_slip) {
        toast.info(`Next slip ${response.data.next_slip.slip_id} auto-assigned to you!`, {
          description: `${response.data.next_slip.total_pieces} pieces, ${response.data.next_slip.total_designs} designs`,
          duration: 5000
        });
      } else if (response.data.auto_assign_status === 'no_pending_slips') {
        toast.info('All pending slips completed! Great work.', { duration: 3000 });
      }
      
      setActiveSlip(null);
      setActiveItem(null);
      setBinInfo(null);
      setBundleCode('');
      loadMySlips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cannot complete yet');
    }
  };
  
  const handleScanSubmit = () => {
    if (!scanInput.trim()) return;
    if (scanMode === 'bin') {
      handleBinScan(scanInput.trim());
    } else if (scanMode === 'usn_first') {
      handleFirstUsnScan(scanInput.trim());
    } else {
      handleUsnScan(scanInput.trim());
    }
    setScanInput('');
  };
  
  // Handle first USN scan to get bin suggestion (for multi-SKU bundles)
  const handleFirstUsnScan = async (usnCode) => {
    try {
      const response = await apiClient.post('/wms/fg/put-away/suggest-bin-for-usn', null, {
        params: { slip_id: activeSlip.slip_id, item_id: activeItem.item_id, usn_code: usnCode }
      });
      
      if (response.data.success && response.data.suggested_bin) {
        // Update the item with the suggested bin and USN details
        setActiveItem(prev => ({
          ...prev,
          suggested_bin: response.data.suggested_bin,
          suggested_bin_info: response.data.suggested_bin_info,
          pending_usn: usnCode,
          pending_usn_barcode: response.data.usn_barcode,
          pending_usn_shade: response.data.usn_shade,
          pending_usn_size: response.data.usn_size
        }));
        
        // Switch to bin scan mode
        setScanMode('bin');
        
        // Show bin suggestion with hierarchy
        const binInfo = response.data.suggested_bin_info;
        if (binInfo?.bin_hierarchy) {
          toast.info(`Scan bin: ${response.data.suggested_bin}`, {
            description: binInfo.bin_hierarchy,
            duration: 6000
          });
        } else {
          toast.info(`Scan bin: ${response.data.suggested_bin}`, {
            description: `USN: ${usnCode} (${response.data.usn_design || 'Unknown'}${response.data.usn_shade ? ' - ' + response.data.usn_shade : ''})`,
            duration: 4000
          });
        }
      } else {
        toast.error(response.data.message || 'Could not get bin suggestion');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get bin suggestion');
    }
  };
  
  const getProgress = () => {
    if (!activeSlip) return 0;
    const total = activeSlip.items?.length || 0;
    const completed = activeSlip.items?.filter(i => i.status === 'completed').length || 0;
    return total > 0 ? (completed / total) * 100 : 0;
  };
  
  const getItemProgress = () => {
    if (!activeItem) return 0;
    const placed = activeItem.placed_usns?.length || 0;
    const total = activeItem.qty || 1;
    return (placed / total) * 100;
  };

  // PROGRESS VIEW - Session Recovery
  if (showProgressView && progressData) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] text-white">
        <header className="bg-gradient-to-r from-blue-600 to-blue-800 shadow-lg">
          <div className="px-4 h-14 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => {
                  setShowProgressView(false);
                  setActiveSlip(null);
                  setProgressData(null);
                }} 
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-lg font-semibold">Session Recovery</h1>
                <p className="text-xs text-blue-200">{progressData.bundle_code || progressData.slip_id}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={refreshProgress}
              disabled={isLoading}
              className="text-white hover:bg-white/10"
            >
              <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </header>
        
        <main className="p-4 space-y-4">
          {/* Progress Summary */}
          <Card className="bg-gradient-to-r from-blue-900/50 to-blue-800/50 border-blue-500/30">
            <CardContent className="pt-4">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <p className="text-sm text-blue-200">Put Away Progress</p>
                  <p className="text-2xl font-bold">{progressData.progress_summary.completed} / {progressData.progress_summary.total_items}</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-blue-300">{progressData.progress_summary.percentage}%</p>
                </div>
              </div>
              <Progress value={progressData.progress_summary.percentage} className="h-3 bg-blue-900" />
              <div className="flex gap-3 mt-3">
                <Badge className="bg-green-500/20 text-green-300">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {progressData.progress_summary.completed} completed
                </Badge>
                <Badge className="bg-orange-500/20 text-orange-300">
                  <Circle className="w-3 h-3 mr-1" />
                  {progressData.progress_summary.pending} pending
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Completed USNs */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-green-400 text-base">
                <CheckCircle2 className="w-5 h-5" />
                Completed USNs ({progressData.completed_usns.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-48 overflow-y-auto space-y-2">
              {progressData.completed_usns.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">No USNs put away yet</p>
              ) : (
                progressData.completed_usns.map((usn, idx) => (
                  <div key={idx} className="bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2">
                    <div className="flex justify-between items-center">
                      <p className="font-mono text-sm text-green-300">{usn.usn_code}</p>
                      <Badge variant="outline" className="text-xs border-green-500/50 text-green-400">
                        {usn.bin_code || usn.actual_bin}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {usn.design_name} | {usn.shade} | {usn.size}
                    </p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Pending USNs */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-orange-400 text-base">
                <Clock className="w-5 h-5" />
                Pending USNs ({progressData.pending_usns.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-48 overflow-y-auto space-y-2">
              {progressData.pending_usns.length === 0 ? (
                <p className="text-gray-500 text-sm text-center py-4">All USNs put away!</p>
              ) : (
                progressData.pending_usns.map((usn, idx) => (
                  <div key={idx} className="bg-orange-500/10 border border-orange-500/30 rounded-lg px-3 py-2">
                    <div className="flex justify-between items-center">
                      <p className="font-mono text-sm text-orange-300">{usn.usn_code}</p>
                      {usn.suggested_bin && (
                        <Badge variant="outline" className="text-xs border-orange-500/50 text-orange-400">
                          → {usn.suggested_bin}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {usn.design_name} | {usn.shade} | {usn.size}
                    </p>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Continue Button */}
          <Button
            onClick={continueFromProgress}
            disabled={isLoading}
            className="w-full h-14 bg-gradient-to-r from-[#2D5F4F] to-[#3d7a64] hover:from-[#3d7a64] hover:to-[#4d8a74] text-lg font-bold rounded-xl"
          >
            {isLoading ? (
              <RefreshCw className="w-6 h-6 animate-spin" />
            ) : (
              <>
                <ScanLine className="w-6 h-6 mr-2" />
                Continue Put Away
              </>
            )}
          </Button>
        </main>
      </div>
    );
  }

  // TROLLEY MODE VIEW - All items at once (Bin first, then USN)
  if (trolleyMode && activeSlip) {
    const pendingItems = activeSlip.items?.filter(i => {
      // Terminal statuses
      if (i.status === 'completed' || i.status === 'skipped' || i.status === 'partial_skipped') return false;
      // Also treat as done if all USNs are placed (handles status sync issues)
      if ((i.placed_usns?.length || 0) >= (i.qty || 0) && i.qty > 0) return false;
      return true;
    }) || [];
    const completedItems = activeSlip.items?.filter(i => i.status === 'completed') || [];
    const skippedItems = activeSlip.items?.filter(i => i.status === 'skipped' || i.status === 'partial_skipped') || [];
    const totalItems = activeSlip.items?.length || 0;
    const completedCount = completedItems.length;
    const skippedCount = skippedItems.length;
    
    return (
      <div className="min-h-screen bg-orange-50">
        <header className="bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-lg">
          <div className="px-4 h-14 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => { 
                  setTrolleyMode(false); 
                  setActiveSlip(null); 
                  setTrolleyActiveItem(null);
                  setTrolleyBinLocked(false);
                  setTrolleyScanMode('select_item');
                }} 
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <p className="font-mono text-sm opacity-80">{activeSlip.slip_id}</p>
                <p className="text-xs font-bold">🛒 TROLLEY MODE</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {skippedCount > 0 && (
                <Badge className="bg-amber-500/80 text-white">
                  {skippedCount} skipped
                </Badge>
              )}
              <Badge className="bg-white/20 text-white">
                {completedCount}/{totalItems} done
              </Badge>
            </div>
          </div>
          <div className="px-4 pb-2">
            <Progress value={((completedCount + skippedCount) / totalItems) * 100} className="h-2 bg-white/20" />
          </div>
        </header>
        
        <main className="p-4 space-y-4">
          {/* Scanner Card - Changes based on mode */}
          <Card className={`border-2 bg-white ${
            trolleyScanMode === 'select_item' ? 'border-gray-300' :
            trolleyScanMode === 'scan_bin' ? 'border-blue-400' : 'border-green-400'
          }`}>
            <CardContent className="pt-4">
              {trolleyScanMode === 'select_item' && (
                <div className="text-center py-4">
                  <Package className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-lg font-bold text-gray-700">Select an Item</p>
                  <p className="text-sm text-gray-500">Tap an item below to start put away</p>
                </div>
              )}
              
              {trolleyScanMode === 'scan_bin' && trolleyActiveItem && (
                <>
                  <div className="text-center mb-3">
                    <MapPin className="w-8 h-8 mx-auto text-blue-500 mb-2" />
                    <p className="text-lg font-bold text-blue-700">Step 1: Scan Bin</p>
                    <p className="text-sm text-gray-600">
                      {trolleyActiveItem.design_name} - {trolleyActiveItem.shade}
                    </p>
                    {trolleyActiveItem.suggested_bin && (
                      <p className="text-xl font-mono font-bold text-blue-800 mt-2">
                        → {trolleyActiveItem.suggested_bin}
                      </p>
                    )}
                  </div>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-400" />
                    <Input
                      ref={scanInputRef}
                      value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && scanInput.trim()) {
                          handleTrolleyScanSubmit();
                        }
                      }}
                      placeholder="Scan bin barcode..."
                      className="pl-10 text-center text-lg h-14 border-2 border-blue-200 focus:border-blue-500"
                      autoFocus
                      data-testid="trolley-bin-input"
                    />
                  </div>
                  <Button
                    onClick={() => {
                      setTrolleyActiveItem(null);
                      setTrolleyScanMode('select_item');
                    }}
                    variant="outline"
                    className="w-full mt-3"
                  >
                    Cancel - Select Different Item
                  </Button>
                </>
              )}
              
              {trolleyScanMode === 'scan_usn' && trolleyActiveItem && (
                <>
                  <div className="text-center mb-3">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                      <span className="text-sm text-green-600 font-medium">
                        Bin {trolleyActiveItem.actual_bin} locked
                      </span>
                    </div>
                    <p className="text-lg font-bold text-green-700">Step 2: Scan USNs</p>
                    <p className="text-sm text-gray-600">
                      {trolleyActiveItem.design_name} - {trolleyActiveItem.shade}
                    </p>
                    <p className="text-lg font-bold mt-1">
                      {trolleyActiveItem.placed_usns?.length || 0} / {trolleyActiveItem.qty} placed
                    </p>
                  </div>
                  <div className="relative">
                    <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-green-400" />
                    <Input
                      ref={scanInputRef}
                      value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && scanInput.trim()) {
                          handleTrolleyScanSubmit();
                        }
                      }}
                      placeholder="Scan USN barcode..."
                      className="pl-10 text-center text-lg h-14 border-2 border-green-200 focus:border-green-500"
                      autoFocus
                      data-testid="trolley-usn-input"
                    />
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      onClick={() => {
                        setTrolleyActiveItem(null);
                        setTrolleyBinLocked(false);
                        setTrolleyScanMode('select_item');
                      }}
                      variant="outline"
                      className="flex-1"
                    >
                      Switch Item
                    </Button>
                    <Button
                      onClick={() => setMarkBinFullDialog(true)}
                      variant="outline"
                      className="flex-1 border-amber-300 text-amber-700"
                    >
                      Bin Full
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          
          {/* All Items Overview */}
          <div className="space-y-3">
            <h3 className="font-bold text-gray-700 flex items-center gap-2">
              <Package className="w-5 h-5" />
              All Items ({pendingItems.length} pending, {completedCount} done{skippedCount > 0 ? `, ${skippedCount} skipped` : ''})
            </h3>
            
            {activeSlip.items?.map((item, idx) => {
              const isCompleted = item.status === 'completed';
              const isSkipped = item.status === 'skipped' || item.status === 'partial_skipped';
              const isActive = trolleyActiveItem?.item_id === item.item_id;
              const placedCount = item.placed_usns?.length || 0;
              
              return (
                <Card 
                  key={item.item_id || idx} 
                  onClick={() => !isCompleted && !isSkipped && handleTrolleySelectItem(item)}
                  className={`border-l-4 cursor-pointer transition-all ${
                    isCompleted 
                      ? 'border-l-green-500 bg-green-50 cursor-default' 
                      : isSkipped
                        ? 'border-l-amber-500 bg-amber-50 cursor-default'
                        : isActive 
                          ? 'border-l-orange-500 bg-orange-100 ring-2 ring-orange-400' 
                          : 'border-l-orange-500 bg-white hover:bg-orange-50'
                  }`}
                  data-testid={`trolley-item-${idx}`}
                >
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-bold">{item.design_name}</p>
                          {isActive && (
                            <Badge className="bg-orange-500 text-white text-xs">ACTIVE</Badge>
                          )}
                          {isSkipped && (
                            <Badge className="bg-amber-500 text-white text-xs">
                              <SkipForward className="w-3 h-3 mr-1" />
                              Skipped
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{item.shade} · {item.size}</p>
                      </div>
                      <div className="text-right">
                        <Badge className={isCompleted ? 'bg-green-500' : isSkipped ? 'bg-amber-500' : 'bg-orange-100 text-orange-800'}>
                          {placedCount}/{item.qty} pcs
                        </Badge>
                        {isSkipped && item.skipped_qty && (
                          <p className="text-xs text-amber-600 mt-1">{item.skipped_qty} skipped</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Skip reason if skipped */}
                    {isSkipped && item.skip_reason && (
                      <div className="bg-amber-100 rounded-lg p-2 mb-2 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-600" />
                        <span className="text-xs text-amber-700">{item.skip_reason}</span>
                      </div>
                    )}
                    
                    {/* Bin Info */}
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-2 mt-2">
                      <div className="flex items-center gap-2">
                        <MapPin className={`w-4 h-4 ${item.actual_bin ? 'text-green-600' : item.suggested_bin ? 'text-blue-600' : 'text-gray-400'}`} />
                        <span className={`font-mono font-bold ${item.actual_bin ? 'text-green-800' : item.suggested_bin ? 'text-blue-800' : 'text-gray-400'}`}>
                          {item.actual_bin ? `✓ ${item.actual_bin}` : item.suggested_bin || 'No bin assigned'}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        {!isCompleted && !isSkipped && (
                          <>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                openSkipDialog(item);
                              }}
                              className="text-xs text-amber-600 border-amber-300 hover:bg-amber-50"
                              title="Skip this item"
                            >
                              <SkipForward className="w-3 h-3" />
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOverrideItem(item);
                                setOverrideBinCode(item.suggested_bin || '');
                                setShowOverrideDialog(true);
                              }}
                              className="text-xs"
                            >
                              Change Bin
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {/* Processed By - shows piece processing staff */}
                    {item.processed_by_name && item.processed_by_name !== 'Goods Transfer' && (
                      <p className="text-xs text-amber-600 mt-2">
                        Processed by: <strong>{item.processed_by_name}</strong>
                      </p>
                    )}
                    {item.source_type === 'goods_transfer' && (
                      <p className="text-xs text-blue-600 mt-2">
                        Source: <strong>Goods Transfer</strong>
                      </p>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {/* Complete Slip Button - Always visible when all items done */}
          {pendingItems.length === 0 && activeSlip.items?.length > 0 && (
            <Button 
              onClick={async () => {
                try {
                  const response = await apiClient.post(`/wms/fg/put-away/complete-slip/${activeSlip.slip_id}`);
                  toast.success('Put away completed!');
                  
                  // Check if next slip was auto-assigned
                  if (response.data.next_slip) {
                    toast.info(`Next slip ${response.data.next_slip.slip_id} auto-assigned to you!`, {
                      description: `${response.data.next_slip.total_pieces} pieces, ${response.data.next_slip.total_designs} designs`,
                      duration: 5000
                    });
                  } else if (response.data.auto_assign_status === 'no_pending_slips') {
                    toast.info('All pending slips completed! Great work.', { duration: 3000 });
                  }
                  
                  setTrolleyMode(false);
                  setActiveSlip(null);
                  setTrolleyActiveItem(null);
                  setTrolleyBinLocked(false);
                  setTrolleyScanMode('select_item');
                  loadMySlips();
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Failed to complete');
                }
              }}
              className="w-full h-14 bg-green-600 hover:bg-green-700 text-lg font-bold"
            >
              <CheckCircle className="w-6 h-6 mr-2" />
              Complete Put Away
            </Button>
          )}
        </main>
        
        {/* Bin Override Dialog */}
        <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Change Bin Assignment</DialogTitle>
              <DialogDescription>
                {overrideItem && `Update bin for: ${overrideItem.design_name}`}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium">New Bin Code</label>
                <Input
                  value={overrideBinCode}
                  onChange={(e) => setOverrideBinCode(e.target.value.toUpperCase())}
                  placeholder="Enter new bin code..."
                  className="mt-1"
                />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowOverrideDialog(false)} className="flex-1">
                  Cancel
                </Button>
                <Button onClick={handleBinOverride} className="flex-1 bg-orange-500 hover:bg-orange-600">
                  Update Bin
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Mark Bin Full Dialog for Trolley Mode */}
        <Dialog open={markBinFullDialog} onOpenChange={setMarkBinFullDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Mark Bin as Full?</DialogTitle>
              <DialogDescription>
                This will record the current quantity and get a new bin suggestion.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Current Bin:</span>
                  <span className="font-mono font-bold">{trolleyActiveItem?.actual_bin}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Design:</span>
                  <span className="font-medium">{trolleyActiveItem?.design_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Items Placed:</span>
                  <span className="font-bold">{trolleyActiveItem?.placed_usns?.length || 0}/{trolleyActiveItem?.qty}</span>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMarkBinFullDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={async () => {
                  if (!trolleyActiveItem) return;
                  try {
                    const response = await apiClient.post('/wms/fg/put-away/mark-bin-full', null, {
                      params: { slip_id: activeSlip.slip_id, item_id: trolleyActiveItem.item_id }
                    });
                    
                    toast.success(`Bin marked full. New bin: ${response.data.suggested_new_bin || 'None'}`);
                    
                    // Update the item
                    const updatedItem = {
                      ...trolleyActiveItem,
                      actual_bin: null,
                      suggested_bin: response.data.suggested_new_bin
                    };
                    setTrolleyActiveItem(updatedItem);
                    setTrolleyBinLocked(false);
                    setTrolleyScanMode('scan_bin');
                    
                    // Update in activeSlip
                    setActiveSlip(prev => ({
                      ...prev,
                      items: prev.items.map(itm => 
                        itm.item_id === trolleyActiveItem.item_id 
                          ? updatedItem
                          : itm
                      )
                    }));
                    
                    setMarkBinFullDialog(false);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to mark bin full');
                  }
                }}
                className="bg-amber-600 hover:bg-amber-700"
              >
                Mark Full & Get New Bin
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Skip Item Dialog */}
        <Dialog open={showSkipDialog} onOpenChange={setShowSkipDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <SkipForward className="w-5 h-5 text-amber-600" />
                Skip Item
              </DialogTitle>
              <DialogDescription>
                Skip this item and send to supervisor for review.
              </DialogDescription>
            </DialogHeader>
            
            {skipItem && (
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Design:</span>
                    <span className="font-medium">{skipItem.design_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Shade/Size:</span>
                    <span className="font-medium">{skipItem.shade} / {skipItem.size}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Total Qty:</span>
                    <span className="font-bold">{skipItem.qty} pcs</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Already Placed:</span>
                    <span className="font-bold text-green-600">{skipItem.placed_usns?.length || 0} pcs</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-sm text-amber-600 font-medium">Will be Skipped:</span>
                    <span className="font-bold text-amber-600">{skipItem.qty - (skipItem.placed_usns?.length || 0)} pcs</span>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Reason *</label>
                  <Select value={skipReason} onValueChange={setSkipReason}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select reason..." />
                    </SelectTrigger>
                    <SelectContent>
                      {skipReasons.map((reason) => (
                        <SelectItem key={reason} value={reason}>
                          {reason}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {skipReason === 'Other' && (
                  <div>
                    <label className="text-sm font-medium">Details</label>
                    <Textarea
                      value={skipReasonDetails}
                      onChange={(e) => setSkipReasonDetails(e.target.value)}
                      placeholder="Please provide details..."
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSkipDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleSkipItem}
                disabled={isSkipping || !skipReason}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {isSkipping ? 'Skipping...' : 'Skip Item'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ACTIVE SLIP VIEW - Put Away in Progress
  if (activeSlip) {
    return (
      <div className="min-h-screen bg-[#FFFBF5]">
        <header className="bg-[#2D5F4F] text-white shadow-lg">
          <div className="px-4 h-14 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleGoBack} 
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <p className="font-mono text-sm opacity-80">{activeSlip.slip_id}</p>
                {activeSlip.bundle_code && (
                  <p className="text-xs font-bold text-yellow-300">{activeSlip.bundle_code}</p>
                )}
              </div>
            </div>
            <Badge className="bg-white/20">{Math.round(getProgress())}%</Badge>
          </div>
          <div className="px-4 pb-2">
            <Progress value={getProgress()} className="h-2 bg-white/20" />
          </div>
        </header>
        
        <main className="p-4 space-y-4">
          {activeItem ? (
            <Card className="border-2 border-[#2D5F4F]">
              <CardHeader className="pb-2 bg-[#2D5F4F]/5">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg">{activeItem.design_name}</CardTitle>
                  <Badge>{activeItem.qty} pcs</Badge>
                </div>
                <CardDescription>
                  {/* Show actual scanned USN shade if available, otherwise bundle default */}
                  {(activeItem.pending_usn_shade || activeItem.shade) && (
                    <span className={activeItem.pending_usn_shade && activeItem.pending_usn_shade !== activeItem.shade ? 'text-green-700 font-medium' : ''}>
                      {activeItem.pending_usn_shade || activeItem.shade} · 
                    </span>
                  )}
                  {(activeItem.pending_usn_size || activeItem.size) && (
                    <span>Size: {activeItem.pending_usn_size || activeItem.size}</span>
                  )}
                  {activeItem.pending_usn_shade && activeItem.pending_usn_shade !== activeItem.shade && (
                    <span className="text-xs text-gray-500 block mt-1">
                      (Bundle contains multiple colors)
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-2 mb-4 text-sm">
                  <div className="bg-gray-50 p-2 rounded">
                    <p className="text-gray-500 text-xs">
                      {activeItem.pending_usn_barcode ? 'USN Barcode' : 'Bundle Barcode'}
                    </p>
                    <p className="font-mono">
                      {activeItem.pending_usn_barcode || activeItem.barcode || '-'}
                    </p>
                    {activeItem.pending_usn_barcode && activeItem.pending_usn_barcode !== activeItem.barcode && (
                      <p className="text-xs text-amber-600 mt-1">
                        (Bundle default: {activeItem.barcode})
                      </p>
                    )}
                  </div>
                  <div className="bg-amber-50 p-2 rounded border border-amber-200">
                    <p className="text-amber-600 text-xs font-medium">📍 Find Pieces At</p>
                    <p className="font-bold text-amber-800">
                      {activeItem.source_type === 'goods_transfer' 
                        ? 'Goods Transfer Area' 
                        : (activeItem.processed_by_name && activeItem.processed_by_name !== 'Auto-System' && activeItem.processed_by_name !== 'system')
                          ? activeItem.processed_by_name
                          : 'Piece Processing Area'}
                    </p>
                  </div>
                </div>
                
                {activeItem.suggested_bin && (
                  <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 rounded-lg">
                    <MapPin className="w-5 h-5 text-blue-600" />
                    <div className="flex-1">
                      <p className="text-sm text-blue-600">Suggested Location</p>
                      {/* Display full location hierarchy: Hall-Row-Rack-Level-Bin */}
                      <p className="font-mono font-bold text-blue-800 text-lg">{activeItem.suggested_bin}</p>
                      {activeItem.suggested_bin && activeItem.suggested_bin.includes('-') && (
                        <div className="text-xs text-blue-600 mt-1">
                          {(() => {
                            const parts = activeItem.suggested_bin.split('-');
                            if (parts.length >= 5) {
                              // FG format: Hall-Row-Rack-Level-Bin (e.g., H06-A-01-A-BIN01)
                              return (
                                <span>
                                  Hall: <strong>{parts[0]}</strong> → Row: <strong>{parts[1]}</strong> → Rack: <strong>{parts[2]}</strong> → Level: <strong>{parts[3]}</strong> → Bin: <strong>{parts[4]}</strong>
                                </span>
                              );
                            } else if (parts.length >= 4) {
                              // Regular format: Hall-Row-Rack-Level (e.g., H06-A-01-A)
                              return (
                                <span>
                                  Hall: <strong>{parts[0]}</strong> → Row: <strong>{parts[1]}</strong> → Rack: <strong>{parts[2]}</strong> → Level: <strong>{parts[3]}</strong>
                                </span>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      )}
                      {activeItem.suggested_bin_info?.suggestion_reason && (
                        <p className="text-xs text-gray-500 mt-1">{activeItem.suggested_bin_info.suggestion_reason}</p>
                      )}
                    </div>
                  </div>
                )}
                
                {binInfo && (
                  <div className="p-3 rounded-lg mb-4 bg-green-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="text-sm text-green-600">Bin Locked</p>
                          <p className="font-mono font-bold">{activeItem.actual_bin}</p>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setMarkBinFullDialog(true)}
                        className="border-amber-300 text-amber-700 hover:bg-amber-50"
                        data-testid="mark-bin-full-btn"
                      >
                        <StopCircle className="w-4 h-4 mr-1" />
                        Bin Full
                      </Button>
                    </div>
                    <div className="text-xs mt-2 flex items-center gap-2">
                      <span className="font-medium">In bin: {binInfo.current_qty || 0}</span>
                      {binInfo.learned_capacity ? (
                        <Badge variant="outline" className="text-xs">
                          Expected: ~{binInfo.learned_capacity}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs bg-blue-50">
                          <Info className="w-3 h-3 mr-1" />
                          No capacity learned yet
                        </Badge>
                      )}
                    </div>
                    {binInfo.approaching_capacity && (
                      <p className="text-xs text-amber-600 mt-1">
                        ⚠️ Approaching expected capacity
                      </p>
                    )}
                  </div>
                )}
                
                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span>{scanMode === 'bin' ? 'Scan Bin' : scanMode === 'usn_first' ? 'Scan First USN' : 'Scan USNs'}</span>
                    <span>{activeItem.placed_usns?.length || 0}/{activeItem.qty}</span>
                  </div>
                  <Progress value={getItemProgress()} className="h-3" />
                </div>
                
                {/* First USN mode instruction */}
                {scanMode === 'usn_first' && (
                  <Alert className="mb-4 border-blue-300 bg-blue-50">
                    <Info className="w-4 h-4 text-blue-600" />
                    <AlertTitle className="text-blue-800">Multi-SKU Bundle</AlertTitle>
                    <AlertDescription className="text-blue-700">
                      <p className="text-sm">Scan the first USN to determine which bin to use. Different SKUs may need different bins.</p>
                    </AlertDescription>
                  </Alert>
                )}
                
                {/* Bin Conflict Resolution UI */}
                {binConflictError && scanMode === 'bin' && (
                  <Alert className="mb-4 border-orange-300 bg-orange-50">
                    <AlertCircle className="w-4 h-4 text-orange-600" />
                    <AlertTitle className="text-orange-800">Location Conflict</AlertTitle>
                    <AlertDescription className="text-orange-700">
                      <p className="mb-3 text-sm">{binConflictError.message}</p>
                      <div className="flex flex-col gap-2">
                        <Button 
                          onClick={handleRequestAlternativeBin}
                          disabled={isRequestingAlternative}
                          className="w-full bg-blue-600 hover:bg-blue-700"
                          data-testid="request-alternative-bin-btn"
                        >
                          {isRequestingAlternative ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <MapPin className="w-4 h-4 mr-2" />
                          )}
                          {isRequestingAlternative ? 'Finding...' : 'Get New Location'}
                        </Button>
                        <p className="text-xs text-center text-gray-500">Or scan a different bin location manually</p>
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
                
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      ref={scanInputRef}
                      value={scanInput}
                      onChange={(e) => setScanInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleScanSubmit()}
                      placeholder={scanMode === 'bin' ? 'Scan bin location' : scanMode === 'usn_first' ? 'Scan first USN' : 'Scan USN'}
                      className="pl-10 h-12"
                      data-testid="scan-input"
                    />
                  </div>
                  <Button onClick={handleScanSubmit} className="h-12 px-6 bg-[#2D5F4F]">
                    <CheckCircle className="w-5 h-5" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="w-4 h-4 text-red-600" />
              <AlertTitle className="text-red-800">No Active Item</AlertTitle>
              <AlertDescription className="text-red-700">
                This slip has no items to put away. Please go back and try again.
              </AlertDescription>
            </Alert>
          )}
          
          <Button onClick={handleCompleteSlip} className="w-full" variant="outline" data-testid="complete-slip-btn">
            Complete Slip
          </Button>
        </main>
        
        {/* Mark Bin Full Confirmation Dialog */}
        <Dialog open={markBinFullDialog} onOpenChange={setMarkBinFullDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Mark Bin as Full?</DialogTitle>
              <DialogDescription>
                This will record the current quantity as the capacity for this design in this bin type.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Current Bin:</span>
                  <span className="font-mono font-bold">{activeItem?.actual_bin}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Design:</span>
                  <span className="font-medium">{activeItem?.design_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Items in Bin:</span>
                  <span className="font-bold text-lg">{binInfo?.current_qty || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Remaining to Place:</span>
                  <span className="font-medium">{activeItem?.qty - (activeItem?.placed_usns?.length || 0)}</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-4">
                System will learn: "{activeItem?.design_name}" fits <strong>{binInfo?.current_qty || 0}</strong> pieces in this bin type.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setMarkBinFullDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleMarkBinFull}
                disabled={isMarkingBinFull}
                className="bg-amber-600 hover:bg-amber-700 text-white"
              >
                {isMarkingBinFull ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <StopCircle className="w-4 h-4 mr-2" />
                    Mark Full & Get New Bin
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // MAIN VIEW - Tabs for My Slips and GT Scan Bundle
  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="px-4 h-14 flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/wms/fg')} 
            className="text-[#F9FAFB] hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-lg font-bold">FG Put Away</h1>
            <p className="text-xs text-[#E8DCC8]">Mobile Interface</p>
          </div>
        </div>
      </header>
      
      <main className="p-4">
        {/* Multi-staff team (optional) — credits pieces to the scanned team round-robin */}
        <Card className="mb-4 border-l-4 border-l-teal-500" data-testid="fg-slip-team-panel">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-teal-600" />
              <p className="text-sm font-semibold text-[#1A1F2E]">Team (optional)</p>
              <span className="text-xs text-gray-400">— leave empty to credit the slip owner</span>
            </div>
            <div className="flex gap-2">
              <Input
                value={staffTeamInput}
                onChange={(e) => setStaffTeamInput(e.target.value)}
                onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddTeamStaff(); } }}
                placeholder="Scan / type staff ID to add"
                className="h-10 text-sm"
                disabled={isAddingStaff}
                data-testid="fg-slip-team-input"
              />
              <Button
                onClick={handleAddTeamStaff}
                disabled={isAddingStaff || !staffTeamInput.trim()}
                className="h-10 bg-teal-600 hover:bg-teal-700 text-white shrink-0"
                data-testid="fg-slip-team-add-btn"
              >
                + Add
              </Button>
            </div>
            {staffTeam.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3" data-testid="fg-slip-team-list">
                {staffTeam.map((s) => (
                  <div key={s.staff_id} className="flex items-center gap-2 bg-teal-50 border border-teal-200 rounded-full pl-3 pr-1 py-1" data-testid={`fg-slip-team-chip-${s.staff_id}`}>
                    <span className="text-xs font-medium text-teal-800">{s.staff_name}</span>
                    <span className="text-[10px] text-teal-600">({s.staff_id})</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTeamStaff(s.staff_id)}
                      className="w-5 h-5 rounded-full bg-teal-100 hover:bg-red-200 flex items-center justify-center text-teal-700 hover:text-red-700 text-xs"
                      data-testid={`fg-slip-team-remove-${s.staff_id}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="my-slips" className="flex items-center gap-2" data-testid="tab-my-slips">
              <List className="w-4 h-4" />
              My Slips
            </TabsTrigger>
            <TabsTrigger value="gt-scan" className="flex items-center gap-2" data-testid="tab-gt-scan">
              <QrCode className="w-4 h-4" />
              GT Scan Bundle
            </TabsTrigger>
          </TabsList>
          
          {/* MY SLIPS TAB */}
          <TabsContent value="my-slips" className="space-y-4">
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={loadMySlips} disabled={isLoading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : mySlips.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Package className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No put away tasks assigned</p>
                  <p className="text-sm text-gray-400 mt-1">Use GT Scan Bundle tab for quick start</p>
                </CardContent>
              </Card>
            ) : (
              mySlips.map(slip => (
                <Card key={slip.slip_id} className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-mono font-bold">{slip.slip_id}</p>
                        {slip.bundle_code && (
                          <p className="text-sm font-bold text-purple-600">{slip.bundle_code}</p>
                        )}
                        <p className="text-sm text-gray-500">{slip.total_pieces} pcs · {slip.items?.length || 0} items</p>
                      </div>
                      <Badge className={
                        slip.status === 'assigned' ? 'bg-blue-100 text-blue-800' :
                        slip.status === 'in_progress' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100'
                      }>
                        {slip.status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        onClick={() => handleStartSlip(slip)}
                        className="bg-[#2D5F4F] hover:bg-[#234A3D]"
                        disabled={slip.status === 'pending'}
                      >
                        {slip.status === 'in_progress' ? 'Continue' : 'Item Mode'}
                      </Button>
                      <Button 
                        onClick={() => handleStartTrolleyMode(slip)}
                        className="bg-orange-500 hover:bg-orange-600"
                        disabled={slip.status === 'pending' || isLoading}
                      >
                        🛒 Trolley Mode
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
          
          {/* GT SCAN BUNDLE TAB */}
          <TabsContent value="gt-scan">
            <Card className="border-0 shadow-lg mb-4">
              <CardHeader className="text-center pb-2">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <QrCode className="w-10 h-10 text-purple-600" />
                </div>
                <CardTitle className="text-xl">Scan Any Bundle</CardTitle>
                <CardDescription>
                  Pick up any GT bundle from staging and scan to start
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="relative">
                  <ScanLine className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-purple-400" />
                  <Input
                    ref={bundleInputRef}
                    value={bundleCode}
                    onChange={(e) => setBundleCode(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleScanBundle()}
                    placeholder="Scan bundle barcode..."
                    className="pl-10 text-center text-lg h-14 border-2 border-purple-200 focus:border-purple-500"
                    data-testid="bundle-code-input"
                  />
                </div>
                <Button 
                  onClick={handleScanBundle}
                  disabled={isScanningBundle || !bundleCode.trim()}
                  className="w-full h-12 bg-purple-600 hover:bg-purple-700"
                  data-testid="scan-bundle-button"
                >
                  {isScanningBundle ? (
                    <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Package className="w-5 h-5 mr-2" />
                  )}
                  {isScanningBundle ? 'Processing...' : 'Start Put Away'}
                </Button>
              </CardContent>
            </Card>
            
            <Alert className="bg-blue-50 border-blue-200">
              <Package className="w-4 h-4 text-blue-600" />
              <AlertTitle className="text-blue-800">How it works</AlertTitle>
              <AlertDescription className="text-blue-700 text-sm">
                <ol className="list-decimal ml-4 space-y-1 mt-2">
                  <li>Pick any bundle from staging area</li>
                  <li>Scan the bundle barcode</li>
                  <li>System creates slip and assigns to you</li>
                  <li>Put away pieces to suggested bin</li>
                </ol>
              </AlertDescription>
            </Alert>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default FGPutAwayMobile;
